import type { ReactNode } from 'react';
import { pickVariant, variables } from '@splunk/themes';
import styled from 'styled-components';
import type { Mode } from '@splunk/dashboard-types';
import { AbsoluteItemContainer } from './AbsoluteItemContainer';

export type BorderStyle =
    | 'selected'
    | 'lowContrastGray'
    | 'highContrastGray'
    | 'none';

export interface GetBorderStyleArgs {
    isSelected: boolean;
    layoutItemType: string;
    mode?: Mode;
    hasDrilldown?: boolean;
    canBeHidden?: boolean;
}

export const getBorderStyle = ({
    isSelected,
    layoutItemType,
    mode = 'view',
    hasDrilldown = false,
    canBeHidden = false,
}: GetBorderStyleArgs): BorderStyle => {
    if (layoutItemType === 'input' && mode === 'view' && isSelected) {
        return 'none';
    }
    if (isSelected) {
        return 'selected';
    }
    if (canBeHidden && mode === 'edit') {
        return 'highContrastGray';
    }
    if (hasDrilldown || mode === 'edit') {
        return 'lowContrastGray';
    }

    return 'none';
};

export interface SelectableContainerProps {
    itemId: string;
    width: string | number;
    height?: string | number;
    children: ReactNode;
    outlineStyle: BorderStyle;
    zIndex?: number;
    canBeHidden?: boolean;
}

export const SelectableContainer = styled.div.attrs<SelectableContainerProps>(
    ({ itemId, width, height, outlineStyle, canBeHidden, zIndex }) => ({
        'data-test': 'select-outline',
        'data-id': itemId,
        'data-outline': canBeHidden ? 'dashed' : 'none',
        'data-outline-style': `${outlineStyle}`,
        style: {
            width,
            height,
            '--outline-layer': zIndex,
        },
    })
)<SelectableContainerProps>`
    display: flex;
    flex-flow: column nowrap;
    position: relative;

    // We position an outline using a pseudo-element absolutely positioned
    &::after {
        content: '';
        position: absolute;
        inset: 0;
        z-index: var(--outline-layer);
        pointer-events: none;
        cursor: default;
        outline-style: ${(props) => (props.canBeHidden ? 'dashed' : 'solid')};
        outline-offset: ${(props) => (props.canBeHidden ? '-2px' : '1px')};
        outline-width: 2px;
        outline-color: ${pickVariant<SelectableContainerProps>('outlineStyle', {
            // NOTE: `focusColor` is not supposed to be applied to UI elements, but everything else
            // changes to "green" brand in Enterprise
            selected: variables.focusColor,
            highContrastGray: variables.interactiveColorBorder,
            lowContrastGray: 'transparent',
            none: 'transparent',
        })};
        opacity: ${pickVariant<SelectableContainerProps>('outlineStyle', {
            highContrastGray: 0.7,
        })};
    }

    ${AbsoluteItemContainer}:hover &::after {
        opacity: 1;
        outline-color: ${pickVariant<SelectableContainerProps>('outlineStyle', {
            lowContrastGray: variables.interactiveColorBorderHover,
            highContrastGray: variables.interactiveColorBorderHover,
        })};
    }

    // Odd behaviors with pickVariant when :hover is applied to a parent instead
    // of this component cause it not to recalculate colors
    ${AbsoluteItemContainer}[data-selected='true']:hover &::after {
        outline-color: ${pickVariant<SelectableContainerProps>('outlineStyle', {
            selected: variables.focusColor,
        })};
    }

    ${AbsoluteItemContainer}[data-selected='true'][data-mute-click-focus='true'] &::after {
        outline-width: 1px;

        outline-color: ${pickVariant<SelectableContainerProps>('outlineStyle', {
            selected: variables.contentColorDisabled,
        })};
    }
`;
